/**
 * Validation manager.
 *
 * @copyright Realeyes OU. All rights reserved.
 */


import EventEmitter from 'events';
import ValidationResult from './ValidationResult';


const STATE_IDLE = 'idle';
const STATE_ACTIVE = 'active';


class ValidationManager extends EventEmitter {
    /**
     * @param {Array.<BaseValidator>} validators List of used validators
     * @param {number} interval Results polling interval in ms
     */
    constructor(validators, interval = 100) {
        super();
        this.validators = validators;
        this.interval = interval;
        this.results = new Map();
        this.state = STATE_IDLE;
    }

    /**
     * @return {Promise}
     */
    start() {
        this.state = STATE_ACTIVE;

        const promises = [];

        for (const validator of this.validators) {
            this.results.set(validator, ValidationResult.NO_RESULT);
            validator.on('result', result => this.results.set(validator, result));
            promises.push(validator.start());
        }

        return Promise.all(promises)
            .then(() => {
                this.intervalId = setInterval(() => {
                    if (this.state !== STATE_ACTIVE) return;

                    this.emit('results', this.getResults());
                }, this.interval);
            });
    }

    /**
     * @return {Promise}
     */
    stop() {
        this.state = STATE_IDLE;

        const promises = [];

        for (const validator of this.validators) {
            validator.removeAllListeners();
            promises.push(validator.stop());
        }

        return Promise.all(promises).then(() => {
            clearInterval(this.intervalId);
            this.results.clear();
        });
    }

    /**
     * @return {Map.<BaseValidator, ValidationResult>}
     */
    getResults() {
        return this.results;
    }
}


export default ValidationManager;
